import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Folder, Clock, Layers, Archive, Search, Filter, MoreVertical, Edit, Trash, Share } from 'lucide-react';
import { api } from '@/services/client';
import { toast } from 'sonner';
import { PageLayout } from '@/components/layout/PageLayout';

interface Session {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  is_active: boolean;
  tab_count: number;
  total_time_seconds: number;
  created_at: string;
  last_opened_at: string;
}

const SessionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [filterActive, setFilterActive] = useState<boolean | null>(null);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await api.get('/sessions');
      setSessions(response.data.sessions || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async (data: { name: string; description: string }) => {
    try {
      await api.post('/sessions', data);
      fetchSessions();
      setShowCreateModal(false);
    } catch (error) {
      console.error('Failed to create session:', error);
    }
  };

  const deleteSession = async (id: string) => {
    toast.promise(
      api.delete(`/sessions/${id}`).then(() => fetchSessions()),
      {
        loading: 'Deleting session...',
        success: 'Session deleted',
        error: 'Failed to delete session',
      }
    );
  };

  const activateSession = async (id: string) => {
    try {
      await api.put(`/sessions/${id}`, { is_active: true });
      const session = sessions.find(s => s.id === id);
      if (session) {
        sessionStorage.setItem('khiimori_session_name', session.name);
      }
      navigate('/monitor');
    } catch {
      toast.error('Failed to open session');
    }
  };

  const shareSession = async (id: string) => {
    try {
      const response = await api.post(`/sessions/${id}/share`);
      toast.success(`Share code: ${response.data.inviteCode}`);
    } catch {
      toast.error('Failed to share session');
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const filteredSessions = sessions.filter(session => {
    if (searchQuery && !session.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterActive !== null && session.is_active !== filterActive) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <PageLayout title="Sessions" description="Manage and organize your browsing sessions" icon={Layers}>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 shadow-lifted"
          >
            <Plus className="h-5 w-5 mr-2" />
            New Session
          </button>
        </div>

        {/* Search and Filters */}
        <div className="mt-6 flex items-center space-x-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-khiimori-text-muted" />
            <input
              type="text"
              placeholder="Search sessions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-khiimori-border/20 rounded-xl bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setFilterActive(null)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterActive === null
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilterActive(true)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterActive === true
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
              }`}
            >
              Active
            </button>
            <button
              onClick={() => setFilterActive(false)}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterActive === false
                  ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                  : 'bg-khiimori-bg-elevated/10 text-khiimori-text-secondary'
              }`}
            >
              Archived
            </button>
          </div>
        </div>
      </div>

      {/* Sessions Grid */}
      {filteredSessions.length === 0 ? (
        <div className="text-center py-12">
          <Folder className="mx-auto h-12 w-12 text-khiimori-text-muted" />
          <h3 className="mt-2 text-sm font-medium text-khiimori-text-primary">No sessions</h3>
          <p className="mt-1 text-sm text-khiimori-text-muted">
            Get started by creating a new session.
          </p>
          <div className="mt-6">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-xl text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Session
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredSessions.map((session) => (
            <div
              key={session.id}
              className="bg-khiimori-bg-elevated/5 backdrop-blur-xl border border-khiimori-border/10 rounded-xl hover:border-khiimori-border/20 hover:shadow-lifted transition-all duration-200 overflow-hidden"
            >
              <div
                className="h-2"
                style={{ backgroundColor: session.color || '#8b5cf6' }}
              ></div>
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3 flex-1">
                    <div className="text-2xl">{session.icon || '📁'}</div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-khiimori-text-primary">
                        {session.name}
                      </h3>
                      {session.description && (
                        <p className="text-sm text-khiimori-text-muted mt-1">
                          {session.description}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="relative">
                    <button className="text-khiimori-text-muted hover:text-khiimori-text-primary">
                      <MoreVertical className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-2 gap-4">
                  <div className="flex items-center text-sm text-khiimori-text-secondary">
                    <Layers className="h-4 w-4 mr-2" />
                    {session.tab_count} tabs
                  </div>
                  <div className="flex items-center text-sm text-khiimori-text-secondary">
                    <Clock className="h-4 w-4 mr-2" />
                    {formatDuration(session.total_time_seconds)}
                  </div>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    session.is_active
                      ? 'bg-success-500/20 text-success-400 border border-success-500/30'
                      : 'bg-khiimori-bg-elevated/20 text-khiimori-text-secondary border border-khiimori-border/30'
                  }`}>
                    {session.is_active ? 'Active' : 'Archived'}
                  </span>
                  <span className="text-xs text-khiimori-text-muted">
                    {new Date(session.last_opened_at || session.created_at).toLocaleDateString()}
                  </span>
                </div>

                <div className="mt-6 flex items-center space-x-2">
                  <button
                    onClick={() => activateSession(session.id)}
                    className="flex-1 px-3 py-2 border border-khiimori-border/20 rounded-lg text-sm font-medium text-khiimori-text-primary bg-khiimori-bg-elevated/10 hover:bg-khiimori-bg-elevated/20 transition-colors"
                  >
                    Open
                  </button>
                  <button
                    onClick={() => shareSession(session.id)}
                    className="p-2 border border-khiimori-border/20 rounded-lg text-khiimori-text-secondary hover:bg-khiimori-bg-elevated/10"
                  >
                    <Share className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => deleteSession(session.id)}
                    className="p-2 border border-error-500/30 rounded-lg text-error-400 hover:bg-error-500/10"
                  >
                    <Trash className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Session Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 modal-backdrop flex items-center justify-center z-50">
          <div className="modal-panel rounded-2xl p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-khiimori-text-primary mb-4">
              Create New Session
            </h2>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.currentTarget);
                createSession({
                  name: formData.get('name') as string,
                  description: formData.get('description') as string,
                });
              }}
            >
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Session Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted focus:ring-2 focus:ring-primary-500"
                    placeholder="e.g., Work Projects"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-khiimori-text-secondary mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-3 py-2 border border-khiimori-border/20 rounded-lg bg-khiimori-bg-elevated/10 text-khiimori-text-primary placeholder-khiimori-text-muted focus:ring-2 focus:ring-primary-500"
                    placeholder="Describe this session..."
                  />
                </div>
              </div>
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-khiimori-border/20 rounded-lg text-sm font-medium text-khiimori-text-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600"
                >
                  Create Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default SessionsPage;
